import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

const CartContext = createContext(null);
const LS_KEY = "xd_cart_v1";
const PROMO_KEY = "xd_promo_v1";

const buildLine = (product, qty, variant) => ({
  cart_key: variant ? `${product.id}::${variant.id}` : product.id,
  product_id: product.id,
  variant_id: variant?.id || null,
  variant_title: variant?.title || null,
  name: product.name,
  price: variant?.price ?? product.price,
  image: variant?.image || (product.images || [])[0] || null,
  quantity: qty,
});

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(LS_KEY) || "[]");
      // Backward-compat: older carts lacked cart_key
      return parsed.map((i) => (i.cart_key ? i : { ...i, cart_key: i.product_id }));
    } catch {
      return [];
    }
  });
  const [promo, setPromo] = useState(() => {
    try { return JSON.parse(localStorage.getItem(PROMO_KEY) || "null"); } catch { return null; }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(LS_KEY, JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (promo) localStorage.setItem(PROMO_KEY, JSON.stringify(promo));
    else localStorage.removeItem(PROMO_KEY);
  }, [promo]);

  const add = useCallback((product, qty = 1, variant = null, opts = {}) => {
    const { openDrawer = true } = opts;
    const displayPrice = variant?.price ?? product.price;
    setItems((cur) => {
      const key = variant ? `${product.id}::${variant.id}` : product.id;
      const idx = cur.findIndex((i) => i.cart_key === key);
      if (idx >= 0) {
        const copy = [...cur];
        copy[idx].quantity += qty;
        return copy;
      }
      return [...cur, buildLine(product, qty, variant)];
    });
    // Fire Meta Pixel AddToCart (no-op if pixel not configured).
    // Dedup: swallow duplicate events for the same product_id within a short
    // window (60s) so the visitor removing-and-readding the same item does not
    // inflate Ads Manager metrics. The event_id CAPI dedup already prevents
    // double-counting between Pixel & CAPI — this dedup is client-side only.
    if (typeof window !== "undefined" && typeof window.audrivioTrack === "function") {
      const DEDUP_WINDOW_MS = 60_000;
      const dedupKey = `audrivio_atc_${product.id}`;
      let shouldFire = true;
      try {
        const last = Number(sessionStorage.getItem(dedupKey) || 0);
        if (last && Date.now() - last < DEDUP_WINDOW_MS) shouldFire = false;
      } catch { /* storage disabled → fire anyway */ }
      if (shouldFire) {
        try { sessionStorage.setItem(dedupKey, String(Date.now())); } catch { /* ignore */ }
        window.audrivioTrack("AddToCart", {
          content_ids: [product.id],
          content_name: product.name,
          content_type: "product",
          currency: "EUR",
          value: Number((displayPrice * qty).toFixed(2)),
        });
      }
    }
    if (openDrawer) setOpen(true);
  }, []);

  // Replace the whole cart (Meta/ads checkout links). No AddToCart tracking.
  const replaceItems = useCallback((lines) => {
    setItems(lines.map(({ product, qty, variant }) => buildLine(product, qty, variant || null)));
  }, []);

  const update = useCallback((cart_key, qty) => {
    setItems((cur) => cur.map((i) => (i.cart_key === cart_key ? { ...i, quantity: Math.max(1, qty) } : i)));
  }, []);

  const remove = useCallback((cart_key) => {
    setItems((cur) => cur.filter((i) => i.cart_key !== cart_key));
  }, []);

  const clear = useCallback(() => { setItems([]); setPromo(null); }, []);

  const applyPromo = useCallback(async (code) => {
    const r = await api.get("/coupons/validate", { params: { code: code.trim() } });
    setPromo(r.data);
    return r.data;
  }, []);

  const removePromo = useCallback(() => setPromo(null), []);

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.quantity, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.quantity, 0), [items]);

  // Gifts are derived (never stored): they appear only while an eligible stereo is in the cart.
  const giftEligible = useMemo(
    () => promo?.kind === "gift" && items.some((i) => i.price > Number(promo.min_item_price ?? 150)),
    [promo, items],
  );
  const gifts = useMemo(() => (giftEligible ? (promo.gifts || []).map((g) => ({
    cart_key: `gift::${g.id}`, product_id: g.id, name: g.name, image: g.image,
    price: 0, list_price: g.list_price, quantity: 1, is_gift: true,
  })) : []), [giftEligible, promo]);

  const value = { items, count, subtotal, add, replaceItems, update, remove, clear, open, setOpen, promo, applyPromo, removePromo, gifts, giftEligible };
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
